import { useCallback, useMemo, useState } from 'react';
import type {
	OEmbedRequiredError,
	ExtractVideoRequest,
	ExtractVideoResponse,
	ExtractVideoAcceptedResponse,
	ExtractVideoNeedsConfirmationResponse,
	RankedCandidate,
} from '../types/api';

function normalizeUrl(raw: string): string {
	let u = raw.trim();
	try {
		const url = new URL(u);
		if (url.hostname === 'youtu.be' && url.pathname.length > 1) {
			const id = url.pathname.replace(/^\//, '');
			u = `https://www.youtube.com/watch?v=${id}`;
		}
	} catch {
		// leave as-is; backend will validate
	}
	return u;
}

type AddVideoResult =
	| { status: 'accepted'; data: ExtractVideoAcceptedResponse }
	| { status: 'needs_confirmation'; data: ExtractVideoNeedsConfirmationResponse }
	| { status: 'oembed_required'; error: OEmbedRequiredError }
	| { status: 'error'; message: string };

export function useAddVideo() {
	const [loading, setLoading] = useState(false);
	const [oembedError, setOembedError] = useState<OEmbedRequiredError | null>(null);
	const [confirm, setConfirm] = useState<ExtractVideoNeedsConfirmationResponse | null>(null);
	const [error, setError] = useState<string | null>(null);

	const reset = useCallback(() => {
		setOembedError(null);
		setConfirm(null);
		setError(null);
	}, []);

	const addVideo = useCallback(
		async (videoUrl: string, opts?: { oembedDomain?: string; cityHint?: string | null; biasLatLng?: { lat: number; lng: number } }): Promise<AddVideoResult> => {
			reset();
			setLoading(true);
			try {
				const url = normalizeUrl(videoUrl);
				const payload: ExtractVideoRequest = {
					action: 'extract-video',
					input: {
						videoUrl: url,
						oembedDomain: opts?.oembedDomain,
						cityHint: opts?.cityHint ?? null,
						biasLat: opts?.biasLatLng?.lat ?? null,
						biasLng: opts?.biasLatLng?.lng ?? null,
					},
				};

				const origin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
				const fnBaseRaw = process.env.REACT_APP_FUNCTIONS_BASE || '';
				const fnBase = fnBaseRaw.replace(/\/$/, '');
				let endpoint = '';
				try {
					endpoint = new URL(`${fnBase}/ai`, origin).toString();
				} catch (e) {
					console.error('[useAddVideo] Failed to build endpoint URL', { fnBaseRaw, origin, error: e });
					throw new Error('No se pudo construir la URL del servidor.');
				}
				console.log('[useAddVideo] Hitting endpoint:', endpoint);
				
				const res = await fetch(endpoint, {
					method: 'POST',
					mode: 'cors',
					cache: 'no-store',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify(payload),
				});

				// Guard: if server is unreachable, fetch may reject earlier; but if we got here and status=0, surface a clear error
				if (res.status === 0) {
					console.error('[useAddVideo] Network error contacting functions', { endpoint });
					throw new Error('No se pudo contactar el servidor de funciones.');
				}

				console.log('🚀 ADDING VIDEO:', { url, payload });
				console.log('📡 RESPONSE STATUS:', res.status, res.statusText);

				const text = await res.text();
				console.log('[useAddVideo] Raw response:', { status: res.status, text: text.slice(0, 1000) });
				let json: ExtractVideoResponse | null = null;
				try { 
					json = JSON.parse(text);
					console.log('[useAddVideo] Parsed JSON:', json);
				} catch (parseError) { 
					console.error('[useAddVideo] JSON parse error:', parseError, 'Raw text:', text.slice(0, 500));
					/* leave null */ 
				}

				if (res.status === 424) {
					const oe = (json as OEmbedRequiredError) || ({
						error: 'oembed_required',
						reason: 'oembed_fetch_failed',
						platform: 'Other',
						debug: { status: res.status, body: text.slice(0, 1200) },
					} as OEmbedRequiredError);
					setOembedError(oe);
					console.info('[useAddVideo] oembed_required', oe);
					return { status: 'oembed_required', error: oe };
				}

				if (!res.ok || !json) {
					let msg = `Server error ${res.status}`;
					if (json && typeof json === 'object' && 'error' in json) {
						msg = `${json.error}`;
						if (json.detail) {
							msg += `: ${json.detail}`;
						}
					} else if (text) {
						msg = `Server error ${res.status}: ${text.slice(0, 200)}`;
					}
					console.error('[useAddVideo] Server error:', { status: res.status, json, text: text.slice(0, 500) });
					setError(msg);
					return { status: 'error', message: msg };
				}

				// Check for accepted response
				if ((json as any).ok && (json as any).accepted) {
					const accepted = json as ExtractVideoAcceptedResponse;
					console.info('[useAddVideo] accepted', { place_id: accepted.google_place_id });
					return { status: 'accepted', data: accepted };
				}

				// Handle weak data case (insufficient restaurant info)
								if ((json as any).ok && (json as any).weak_data) {
					console.log('🔍 WEAK DATA RESPONSE DETAILS:', {
						reason: (json as any).reason,
						extracted_signals: (json as any).extracted_signals,
						whisper_attempted: (json as any).whisper_attempted,
						whisper_transcript: (json as any).whisper_transcript,
						transcription_service: (json as any).transcription_service,
						oembed: (json as any).oembed,
						fullResponse: json
					});
					
					// Log the exact flow that led to this failure
					console.log('🔍 FLOW ANALYSIS:', {
										platform: (json as any).oembed?.provider_name,
										hasTitle: !!(json as any).oembed?.title,
										title: (json as any).oembed?.title,
										hasTranscript: Boolean((json as any).has_transcript) || !!(json as any).whisper_transcript,
										transcriptLength: (json as any).transcript_len || (json as any).whisper_transcript?.length || 0,
										transcriptionService: (json as any).transcript_service || (json as any).transcription_service,
						restaurantName: (json as any).extracted_signals?.restaurantName,
						reason: (json as any).reason
					});
					
										// Enhanced error message with more details
					let errorMessage = 'Insufficient restaurant data';
										const reason: string = (json as any).reason || '';
										if (reason === 'insufficient_restaurant_data' || reason === 'insufficient_restaurant_data_facebook_no_transcript') {
						const restaurantName = (json as any).extracted_signals?.restaurantName;
										const hasTranscript = Boolean((json as any).has_transcript) || !!(json as any).whisper_transcript;
										const transcriptLength = (json as any).transcript_len || (json as any).whisper_transcript?.length || 0;
											const platform = ((json as any).platform || (json as any).oembed?.provider_name || '').toLowerCase();
										const service = (json as any).transcript_service || (json as any).transcription_service;
						
						if (restaurantName === 'Unknown Restaurant') {
												// Facebook: we do not support transcription; rely on OCR/oEmbed/title/hashtags/description
												if (platform === 'facebook' || reason === 'insufficient_restaurant_data_facebook_no_transcript') {
													errorMessage = 'No restaurant name detected. For Facebook videos, we rely on OCR, oEmbed (title/thumbnail), hashtags, and description—captions are not supported.';
												} else {
													errorMessage = `No restaurant name detected. ${hasTranscript ? `Transcript found (${transcriptLength} chars via ${service || 'unknown'}) but no restaurant mentioned.` : 'No transcript available.'}`;
												}
							
							// Add platform-specific transcript info
											if (platform === 'youtube') {
												if (!hasTranscript) {
													errorMessage += ' YouTube: Public captions may not be available for this video. We already try timedtext automatically when present.';
												} else if (service && String(service).toLowerCase().includes('timedtext')) {
													errorMessage += ' YouTube: Captions were found via timedtext but didn\'t include a clear restaurant name.';
												}
							} else if (platform === 'tiktok' && !hasTranscript) {
								errorMessage += ' TikTok: Audio transcription should work. Try videos where restaurant name is mentioned in audio.';
							} else if (platform === 'instagram' && !hasTranscript) {
								errorMessage += ' Instagram: Audio transcription should work. Try videos with restaurant name in audio or caption.';
												} else if (platform === 'facebook') {
													errorMessage += ' Facebook: Captions/transcription are not used; try videos with visible signage, a descriptive title, or restaurant hashtags.';
							}
						} else {
												if (platform === 'facebook' || reason === 'insufficient_restaurant_data_facebook_no_transcript') {
													errorMessage = `Restaurant \"${restaurantName}\" detected but insufficient data for search. For Facebook, we rely on OCR/oEmbed/hashtags/description.`;
												} else {
													errorMessage = `Restaurant \"${restaurantName}\" detected but insufficient data for search. ${hasTranscript ? `Transcript available (${transcriptLength} chars${service ? ` via ${service}` : ''}).` : 'No transcript available.'}`;
												}
						}
						
						// Add platform-specific tips
						if (platform === 'tiktok') {
							errorMessage += ' TikTok: Try videos where the restaurant name is mentioned in audio or text.';
						} else if (platform === 'instagram') {
							errorMessage += ' Instagram: Try videos with restaurant name in caption or audio.';
						} else if (platform === 'youtube') {
											errorMessage += ' YouTube: Try videos with restaurant name in title/description or visible signage.';
											} else if (platform === 'facebook') {
												errorMessage += ' Facebook: Ensure the title/thumbnail has the restaurant name or include hashtags with the name.';
						}
					}
					
					throw new Error(errorMessage);
				}

				// Check for confirmation needed response
				if ((json as any).ok && (json as any).needs_confirmation) {
					const needs = json as ExtractVideoNeedsConfirmationResponse;
					setConfirm(needs); // Store the full response
					console.info('[useAddVideo] needs_confirmation', { count: needs.top.length });
					return { status: 'needs_confirmation', data: needs };
				}

				// Check for error response
				if ((json as any).error) {
					const errorMsg = (json as any).error || 'Server error';
					console.error('[useAddVideo] Server error response:', json);
					setError(errorMsg);
					return { status: 'error', message: errorMsg };
				}

				console.error('[useAddVideo] Unexpected response format:', {
					hasOk: 'ok' in (json || {}),
					hasAccepted: 'accepted' in (json || {}),
					hasWeakData: 'weak_data' in (json || {}),
					hasNeedsConfirmation: 'needs_confirmation' in (json || {}),
					hasError: 'error' in (json || {}),
					allKeys: Object.keys(json || {}),
					json
				});
				const msg = `Unexpected response format. Keys: ${Object.keys(json || {}).join(', ')}`;
				setError(msg);
				return { status: 'error', message: msg };
			} catch (e: any) {
				let msg = String(e?.message || e || 'Unknown error');
				// Surface common browser/network errors clearly
				if (/Failed to fetch|NetworkError|TypeError: Failed to fetch/.test(msg)) {
					msg = 'Load failed: network/CORS error contacting the functions endpoint.';
				}
				setError(msg);
				return { status: 'error', message: msg };
			} finally {
				setLoading(false);
			}
		},
		[reset]
	);

	return useMemo(
		() => ({
			loading,
			oembedError,
			confirm,
			error,
			addVideo,
			reset,
		}),
		[loading, oembedError, confirm, error, addVideo, reset]
	);
}

export default useAddVideo;


